import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, UserPlus, Search, RefreshCw, X, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLES = {
  active: "border-volt/40 bg-volt/10 text-volt",
  expiring: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  expired: "border-red-500/40 bg-red-500/10 text-red-400",
};

const inputCls =
  "w-full rounded-lg border border-zinc-700 bg-ink px-4 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt";

const MembersTab = () => {
  const today = new Date().toISOString().slice(0, 10);
  const EMPTY = { name: "", phone: "", package_slug: "monthly", batch: "morning", start_date: today };
  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const fileRef = useRef(null);

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/members/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.added} member(s) imported`, {
        description: data.skipped ? `${data.skipped} skipped (${data.errors[0] || "duplicates/invalid"})` : undefined,
      });
      load(search);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get("/members", { params });
      setMembers(data);
    } catch {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load("");
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = statusFilter ? members.filter((m) => m.computed_status === statusFilter) : members;

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/members", form);
      toast.success("Member added");
      setShowAdd(false);
      setForm(EMPTY);
      load(search);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const renew = async (id) => {
    try {
      await api.post(`/members/${id}/renew`);
      toast.success("Membership renewed");
      load(search);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="mt-6" data-testid="members-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="member-search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
            placeholder="Search by name or phone..."
            className="w-full rounded-full border border-zinc-700 bg-surface py-3 pl-11 pr-4 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {["", "active", "expiring", "expired"].map((s) => (
            <button
              key={s || "all"}
              data-testid={`member-filter-${s || "all"}-button`}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full border px-4 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
                statusFilter === s ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {s || "All"}
            </button>
          ))}
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="member-import-file" />
          <button
            data-testid="import-csv-button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 rounded-full border border-volt/60 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-ink disabled:opacity-60"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import CSV
          </button>
          <button
            data-testid="add-member-button"
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 rounded-full bg-volt px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all hover:bg-volt-hover"
          >
            {showAdd ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {showAdd ? "Close" : "Add Member"}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={addMember} className="mt-5 grid grid-cols-1 gap-4 rounded-xl border border-volt/30 bg-surface p-6 sm:grid-cols-2 lg:grid-cols-5" data-testid="add-member-form">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Name *</label>
            <input data-testid="member-name-input" required minLength={2} className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Member name" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Phone *</label>
            <input data-testid="member-phone-input" required className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="98XXXXXXXX" inputMode="numeric" />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Package *</label>
            <select data-testid="member-package-select" className={inputCls} value={form.package_slug} onChange={(e) => setForm({ ...form, package_slug: e.target.value })}>
              {packages.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name_en} — ₹{p.price.toLocaleString("en-IN")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Batch</label>
            <select data-testid="member-batch-select" className={inputCls} value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })}>
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
              <option value="night">Night</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Start Date</label>
            <div className="flex gap-2">
              <input data-testid="member-startdate-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <button data-testid="member-save-button" type="submit" disabled={saving} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-volt px-4 font-display text-xs font-bold uppercase tracking-widest text-ink disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="mt-5 hidden overflow-x-auto rounded-xl border border-zinc-800 md:block" data-testid="members-table-wrapper">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-zinc-500" data-testid="members-empty">No members found.</p>
        ) : (
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="members-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-surface font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                <th className="px-5 py-4">Member</th>
                <th className="px-5 py-4">Phone</th>
                <th className="px-5 py-4">Package</th>
                <th className="px-5 py-4">Batch</th>
                <th className="px-5 py-4">Start</th>
                <th className="px-5 py-4">Expiry</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-zinc-800/60 hover:bg-surface/60" data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-4 font-semibold text-white">{m.name}</td>
                  <td className="px-5 py-4 font-mono text-zinc-400">{m.phone}</td>
                  <td className="px-5 py-4 text-zinc-300">{m.package_name_en}</td>
                  <td className="px-5 py-4 capitalize text-zinc-400">{m.batch}</td>
                  <td className="px-5 py-4 text-xs text-zinc-500">{fmt(m.start_date)}</td>
                  <td className="px-5 py-4 text-xs text-zinc-400">{fmt(m.expiry_date)}</td>
                  <td className="px-5 py-4">
                    <span className={`rounded-full border px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLES[m.computed_status]}`} data-testid={`member-status-${m.id}`}>
                      {m.computed_status}{m.computed_status !== "expired" && m.days_left <= 7 ? ` (${m.days_left}d)` : ""}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <button data-testid={`member-renew-${m.id}`} onClick={() => renew(m.id)} className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-volt hover:text-volt">
                      <RefreshCw className="h-3 w-3" /> Renew
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-5 space-y-3 md:hidden" data-testid="members-cards">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
        ) : filtered.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 py-16 text-center text-sm text-zinc-500">No members found.</p>
        ) : (
          filtered.map((m) => (
            <div key={m.id} className="rounded-xl border border-zinc-800 bg-surface p-4" data-testid={`member-card-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{m.name}</p>
                  <p className="font-mono text-xs text-zinc-400">{m.phone}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLES[m.computed_status]}`}>
                  {m.computed_status}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-zinc-400">
                <span>{m.package_name_en} • <span className="capitalize">{m.batch}</span></span>
                <span>Expiry: {fmt(m.expiry_date)}</span>
              </div>
              <button data-testid={`member-renew-mobile-${m.id}`} onClick={() => renew(m.id)} className="mt-3 flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-zinc-300 hover:border-volt hover:text-volt">
                <RefreshCw className="h-3 w-3" /> Renew
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MembersTab;
